import moment from 'moment';

export interface PaymentSectionVisibilityInput {
  accountType: string | null | undefined;
  expiration: string | Date | null | undefined;
  accountExpired?: boolean;
  existingSubscriptionId?: string | false | null;
}

/** Subscription / trial end is today, within the next 30 days, or already past (renewal window). */
export function isExpirationWithinWindow(expiration: string | Date | null | undefined): boolean {
  if (expiration == null || expiration === '') {
    return false;
  }
  const exp = moment(expiration, ['YYYY-MM-DD', moment.ISO_8601], true);
  if (!exp.isValid()) {
    const fallback = moment(expiration);
    if (!fallback.isValid()) {
      return false;
    }
    return fallback.startOf('day').diff(moment().startOf('day'), 'days') <= 30;
  }
  const daysUntilExpiration = exp.startOf('day').diff(moment().startOf('day'), 'days');
  return daysUntilExpiration <= 30;
}

/** Trial/subscription end is today or within the next 30 days (not yet lapsed). */
export function isExpirationApproaching(expiration: string | Date | null | undefined): boolean {
  if (expiration == null || expiration === '') {
    return false;
  }
  const exp = moment(expiration, ['YYYY-MM-DD', moment.ISO_8601], true);
  const parsed = exp.isValid() ? exp : moment(expiration);
  if (!parsed.isValid()) {
    return false;
  }
  const daysUntilExpiration = parsed.startOf('day').diff(moment().startOf('day'), 'days');
  return daysUntilExpiration >= 0 && daysUntilExpiration <= 30;
}

export function hasExpirationDate(expiration: string | Date | null | undefined): boolean {
  if (expiration == null || expiration === '') {
    return false;
  }
  return moment(expiration, ['YYYY-MM-DD', moment.ISO_8601], true).isValid() || moment(expiration).isValid();
}

/**
 * Payment & billing tab visibility (requires freeToPremium toggle to be checked separately).
 * Trial: show when the account has a Braintree subscription, or when subscription_end_date is within 30 days.
 * Non-Free (paid types): always. Free: only when renewing a lapsed subscription.
 */
export function normalizeSubscriptionStatus(status: string | null | undefined): string {
  return String(status ?? '')
    .trim()
    .toLowerCase();
}

export function isSubscriptionCanceled(status: string | null | undefined): boolean {
  const normalized = normalizeSubscriptionStatus(status);
  return normalized === 'canceled' || normalized === 'cancelled';
}

export function isSubscriptionPastDue(status: string | null | undefined): boolean {
  return normalizeSubscriptionStatus(status) === 'past due';
}

export function isSubscriptionExpired(status: string | null | undefined): boolean {
  return normalizeSubscriptionStatus(status) === 'expired';
}

export type SubscriptionSubmitAction = 'create' | 'resubscribe' | 'update';

/** Primary action for the payment page submit control. */
export function getSubscriptionSubmitAction(input: {
  existingSubscriptionId: string | false | null | undefined;
  subscriptionStatus: string | null | undefined;
}): SubscriptionSubmitAction {
  if (!input.existingSubscriptionId) {
    return 'create';
  }
  if (
    isSubscriptionCanceled(input.subscriptionStatus) ||
    isSubscriptionExpired(input.subscriptionStatus)
  ) {
    return 'resubscribe';
  }
  return 'update';
}

/** Which subscription API to call when saving billing changes, if any. */
export function getSubscriptionPersistMethod(input: {
  existingSubscriptionId: string | false | null | undefined;
  subscriptionStatus: string | null | undefined;
}): 'create' | 'update' | null {
  const action = getSubscriptionSubmitAction(input);
  if (action === 'create' || action === 'resubscribe') {
    return 'create';
  }
  if (action === 'update' && isSubscriptionPastDue(input.subscriptionStatus)) {
    return 'update';
  }
  return null;
}

export function shouldShowPaymentSection(input: PaymentSectionVisibilityInput): boolean {
  const type = input.accountType;
  if (!type || type === 'Free') {
    const hadSubscription = !!(input.existingSubscriptionId || input.accountExpired);
    return hadSubscription && isExpirationWithinWindow(input.expiration);
  }
  if (type === 'Trial') {
    if (input.existingSubscriptionId) {
      return true;
    }
    return hasExpirationDate(input.expiration) && isExpirationWithinWindow(input.expiration);
  }
  return true;
}

/** Whether the user/account may start a one-time free trial. */
export function canStartFreeTrial(input: {
  accountType?: string | null;
  subscriptionStartDate?: string | Date | null;
  premiumTrialStart?: string | Date | null;
}): boolean {
  if (input.accountType !== 'Free') {
    return false;
  }
  if (input.subscriptionStartDate != null && input.subscriptionStartDate !== '') {
    return false;
  }
  if (input.premiumTrialStart != null && input.premiumTrialStart !== '') {
    return false;
  }
  return true;
}
